import * as React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useQueryClient } from "@tanstack/react-query";
import { Button, FormField, Input, Logo, useToast } from "@chotca/ui";
import { vi } from "@chotca/config";
import { getSdk, useMe } from "@chotca/api-client";
import { AppBootLoader } from "@chotca/app-shell";
import { setAccessToken } from "@/boot/access-token";
import { WEB_APEX_URL } from "@/env";
import { errMsg } from "./auth-errors";

/**
 * Admin login page. Mirrors the employer login layout but with admin-tinted
 * copy and no register/employer-cta footer — admin accounts are provisioned
 * internally, not self-served.
 */
export function AdminLoginPage() {
  const t = useTranslations("auth");
  const { error: toastError } = useToast();
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [redirect] = React.useState(() => {
    if (typeof window === "undefined") return null;
    const sp = new URLSearchParams(window.location.search);
    return sp.get("redirect") ?? sp.get("next");
  });

  if (me.isLoading) return <AppBootLoader />;
  if (me.data?.globalRole === "ADMIN") {
    return (
      <Navigate
        to={
          redirect && redirect.startsWith("/") && !redirect.startsWith("//")
            ? redirect
            : "/todo"
        }
        replace
      />
    );
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setError(null);
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") ?? "").trim();
    const password = String(fd.get("password") ?? "");
    try {
      const res = await getSdk().auth.loginWithPassword({ email, password });
      if (res.user.globalRole !== "ADMIN") {
        // Revoke the session we just created — otherwise the non-admin
        // user still has a valid refresh cookie scoped to .chotca.com.
        await getSdk()
          .auth.logout({ refreshToken: res.refreshToken })
          .catch(() => undefined);
        setError("Tài khoản này không có quyền quản trị.");
        toastError("Tài khoản này không có quyền quản trị.");
        setPending(false);
        return;
      }
      setAccessToken(res.accessToken);
      qc.setQueryData(["me"], res.user);
      const target =
        redirect && redirect.startsWith("/") && !redirect.startsWith("//")
          ? redirect
          : "/todo";
      nav(target, { replace: true });
    } catch (err) {
      const msg = errMsg(err, "Đăng nhập thất bại");
      setError(msg);
      toastError(msg);
      setPending(false);
    }
  }

  return (
    <main className="min-h-screen bg-paper text-ink">
      <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 lg:grid-cols-[1fr_1fr]">
        <aside className="relative hidden overflow-hidden bg-ink p-12 text-paper lg:flex lg:flex-col lg:justify-between">
          <a
            href={WEB_APEX_URL}
            aria-label={vi.common.appName}
            className="inline-flex w-fit"
          >
            <Logo variant="horizontal" size={28} onDark />
          </a>

          <div>
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-lime">
              KHU QUẢN TRỊ
            </p>
            <h2 className="mt-5 font-satoshi text-[clamp(40px,5vw,64px)] font-black leading-[0.96] tracking-tight">
              Vận hành
              <br />
              Chốt Ca
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-paper/70">
              Trang quản trị nội bộ. Chỉ tài khoản được cấp quyền admin mới có
              thể truy cập.
            </p>
          </div>

          <div className="text-xs text-paper/40">
            Cần hỗ trợ? Liên hệ team kỹ thuật.
          </div>
        </aside>

        <section className="flex flex-col justify-center px-6 py-12 lg:px-16">
          <a
            href={WEB_APEX_URL}
            className="mb-10 inline-flex w-fit items-center gap-2 lg:hidden"
          >
            <Logo variant="square" size={32} />
            <span className="font-satoshi text-lg font-extrabold">
              {vi.common.appName}
            </span>
          </a>

          <div className="mx-auto w-full max-w-md">
            <p className="text-label font-bold uppercase tracking-[0.12em] text-ash">
              ADMIN
            </p>
            <h1 className="mt-3 font-satoshi text-4xl font-black leading-[0.96] tracking-tight md:text-5xl">
              Đăng nhập quản trị
            </h1>
            <p className="mt-3 text-base text-ink/70">
              Dành riêng cho team vận hành Chốt Ca.
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4">
              <FormField label={t("emailLabel")}>
                <Input
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  autoFocus
                />
              </FormField>
              <FormField label={t("passwordLabel")}>
                <Input
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                />
              </FormField>

              {error ? (
                <p className="rounded-xl bg-danger/10 p-3 text-sm text-danger">
                  {error}
                </p>
              ) : null}

              <Button type="submit" fullWidth loading={pending} size="lg">
                {t("loginCta")}
              </Button>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}
